import {Component, computed, ElementRef, OnInit, signal, viewChild} from '@angular/core';
import {Location} from '@angular/common';
import {ReservationService} from '../shared/services/reservation.service';
import {DateUtilsService} from '../shared/services/date-utils.service';
import {AuthService} from '../shared/services/auth-service.service';
import {IlotDto} from '../shared/dto/ilot.dto';
import {finalize} from 'rxjs/operators';
import {AlertService, isNotNullOrUndefined} from 'sigale-angular';
import {TranslateService} from '@ngx-translate/core';
import {ActivatedRoute} from '@angular/router';
import {Ilot} from './model/ilot.model';
import {Bureau} from './model/bureau.model';
import {Duree} from '../shared/models/duree.model';
import {Mode} from '../shared/components/selection-zone/model/mode.enum';
import {EtatPageZoneDeTravail} from './model/etat-page-zone-de-travail.model';
import {UtilisateurSigalett} from '../shared/models/utilisateur-sigalett.model';

@Component({
	selector: 'app-reserver-un-bureau',
	templateUrl: './reserver-un-bureau.component.html',
	styleUrl: './reserver-un-bureau.component.scss'
})
export class ReserverUnBureauComponent implements OnInit {
	readonly ALERTE_RESERVATION_BUREAU = 'alertReservationBureau';

	gridContainer = viewChild<ElementRef>('gridContainer');

	dateReservation = signal<Date>(new Date());
	duree = signal<Duree>(Duree.JOURNEE);

	ilots = signal<Ilot[]>([]);
	loading = signal<boolean>(false);
	estIlotSelectionne = signal<boolean>(false);
	ilotSelectionne = signal<Ilot>(null);

	longueurParent = signal<number>(50);
	hauteurParent = signal<number>(50);

	labelHaut = signal<string>('');
	labelDroite = signal<string>('');
	labelBas = signal<string>('');
	labelGauche = signal<string>('');

	estAffichageVertical = signal<boolean>(false);

	etatVisionnage = signal<EtatPageZoneDeTravail>(null);
	estModeReservation = computed(() => this.etatVisionnage()!=null && this.etatVisionnage()===EtatPageZoneDeTravail.RESERVER_BUREAU);

	bureauPreferentielUtilisateurCourant = signal<number | null>(null);

	langueUtilisateur: string;
	utilisateur: UtilisateurSigalett;
	private zoneSelectionneeId: number;

	constructor(
			private dateUtilsService: DateUtilsService,
			private authService: AuthService,
			private reserverUnBureauService: ReservationService,
			private alertService: AlertService,
			private location: Location,
			private translateService: TranslateService,
			private route: ActivatedRoute
	) {
	}

	ngOnInit(): void {
		const utilisateur = this.authService.getUtilisateurConnecte();

		this.utilisateur = utilisateur;
		this.langueUtilisateur = utilisateur.langue;

		this.recupererBureauPreferentielPourUtilisateurCourant();

		this.route.data.subscribe(data => {
			this.etatVisionnage.set(data.contexteAppel);

			this.route.queryParamMap.subscribe(params => {
				const dateReservationISOString = params.get('date');
				if (dateReservationISOString) {
					this.dateReservation.set(new Date(dateReservationISOString));
				}

				if (this.etatVisionnage()===EtatPageZoneDeTravail.RESERVER_BUREAU) {
					const duree = params.get('duree');
					if (duree) {
						this.duree.set(duree as Duree);
					}
				}
			});
		});
	}

	recupererBureauPreferentielPourUtilisateurCourant(): void {
		this.reserverUnBureauService.recupererBureauPreferentielUtilisateurConnecte(this.utilisateur.login)
				.subscribe({
					next: bureauPreferentiel => {
						if (bureauPreferentiel!=null) {
							this.bureauPreferentielUtilisateurCourant.set(bureauPreferentiel.id);
						}
					},
					error: () => {
						// Tant pis
					}
				});
	}

	/**
	 * Récupère les ilots et les bureaux pour la réservation
	 */
	recupererIlotsEtBureaux(): void {
		this.loading.set(true);
		const dateReservation = this.dateUtilsService.formaterDateEnStringPourEnvoiBack(this.dateReservation(), this.langueUtilisateur);

		this.reserverUnBureauService.recupererIlotsEtBureauxPourDateReservation(this.zoneSelectionneeId, dateReservation)
				.pipe(finalize(() => this.loading.set(false)))
				.subscribe({
					next: (zoneDeTravailAvecIlotsDto) => {

						let ilots = this.convertirIlotDtoEnIlot(zoneDeTravailAvecIlotsDto.ilots);

						const longueurEcran = window.innerWidth;
						const hauteurEcran = window.innerHeight;

						const longeurPlan = Math.max(...ilots.flatMap(ilot => ilot.bureaux).map(bureau => bureau.coordonneeX));
						const largeurPlan = Math.max(...ilots.flatMap(ilot => ilot.bureaux).map(bureau => bureau.coordonneeY));

						const indicateurHaut = zoneDeTravailAvecIlotsDto.indicateurHaut ?? '';
						const indicateurDroite = zoneDeTravailAvecIlotsDto.indicateurDroite ?? '';
						const indicateurBas = zoneDeTravailAvecIlotsDto.indicateurBas ?? '';
						const indicateurGauche = zoneDeTravailAvecIlotsDto.indicateurGauche ?? '';

						const estMobile = longueurEcran < hauteurEcran;
						const estPlanHorizontal = longeurPlan >= largeurPlan;

						if (estMobile) {
							this.estAffichageVertical.set(true);
						}

						if ((estMobile && estPlanHorizontal) || (!estMobile && !estPlanHorizontal)) {
							ilots = this.tournerPlanA90DegreSensHoraire(ilots);

							this.labelDroite.set(indicateurHaut);
							this.labelBas.set(indicateurDroite);
							this.labelGauche.set(indicateurBas);
							this.labelHaut.set(indicateurGauche);
						} else {
							this.labelHaut.set(indicateurHaut);
							this.labelDroite.set(indicateurDroite);
							this.labelBas.set(indicateurBas);
							this.labelGauche.set(indicateurGauche);
						}

						this.ilots.set(ilots);

						const longueurContainer = this.gridContainer().nativeElement.offsetWidth;
						const hauteurContainer = this.gridContainer().nativeElement.offsetHeight;

						this.longueurParent.set(longueurContainer);
						this.hauteurParent.set(hauteurContainer);

						if (this.zoneDeTravailPossedeUnSeulIlot()) {
							this.ilotSelectionne.set(this.ilots()[0]);
							this.estIlotSelectionne.set(true);
						}
					},
					error: (error) => {
						this.alertService.clear(this.ALERTE_RESERVATION_BUREAU);
						this.alertService.error(error.error, this.ALERTE_RESERVATION_BUREAU);
					}
				});
	}

	convertirIlotDtoEnIlot(ilotsDto: IlotDto[]): Ilot[] {
		const ilots: Ilot[] = [];
		ilotsDto.forEach(ilotDto => {
			const bureaux: Bureau[] = [];

			ilotDto.bureaux.forEach(bureauDto => {
				bureaux.push(new Bureau(this.translateService, bureauDto, bureauDto.coordonneeX, bureauDto.coordonneeY));
			});

			ilots.push(new Ilot(ilotDto, bureaux));
		});

		return ilots;
	}

	/**
	 * Tourne le plan à 90 degrés dans le sens horaire pour l'affichage sur mobile.
	 * @param ilots
	 */
	tournerPlanA90DegreSensHoraire(ilots: Ilot[]): Ilot[] {
		const ilotsTournesA90Degre: Ilot[] = [];

		const coordonneesY = ilots.flatMap(ilot => ilot.bureaux).map(bureau => bureau.coordonneeY);

		const minY = Math.min(...coordonneesY);

		ilots.forEach(ilot => {

			const bureaux: Bureau[] = [];

			ilot.bureaux.forEach(bureau => {
				const nouveauX = 2 - (bureau.coordonneeY - minY);
				const nouveauBureau = new Bureau(this.translateService, bureau, nouveauX, bureau.coordonneeX);

				bureaux.push(nouveauBureau);
			});

			ilotsTournesA90Degre.push(new Ilot(ilot, bureaux));
		});

		return ilotsTournesA90Degre;
	}

	/**
	 * Détermine le label du bouton de retour.
	 */
	determinerLabelRetour(): string {
		if (this.estIlotSelectionne() && !this.zoneDeTravailPossedeUnSeulIlot()) {
			return this.translateService.instant('RESERVER_UN_BUREAU.BTN_REVENIR_SELECTION_ILOTS');
		}

		return this.translateService.instant('RESERVER_UN_BUREAU.BTN_REVENIR_ACCUEIL');
	}

	/**
	 * Action permettant de revenir sur l'écran précédent. Ecran de sélection des ilots si on est sur l'écran de sélection de bureau
	 * et la page d'accueil si on est déjà sur l'écran de sélection des ilots.
	 */
	revenirEcranPrecedent(): void {
		if (this.estIlotSelectionne() && !this.zoneDeTravailPossedeUnSeulIlot()) {
			this.ilotSelectionne.set(null);
			this.estIlotSelectionne.set(false);
			return;
		}

		this.location.back();
	}

	/**
	 * True si la zone de travail ne possède qu'un seul Ilot, false sinon.
	 */
	zoneDeTravailPossedeUnSeulIlot(): boolean {
		return this.ilots().length===1;
	}

	/**
	 * Permet d'afficher la date de réservation au bon format.
	 */
	recupererDateReservationBonFormat(): string {
		return this.dateUtilsService.formaterDateEnFormatLettre(this.dateReservation(), this.langueUtilisateur);
	}

	/**
	 * Sélectionne un ilot par son id.
	 *
	 * @param id l'id de l'ilot.
	 */
	selectionnerIlot(id: number): void {
		this.estIlotSelectionne.set(true);
		const ilot = this.ilots().find(ilot => ilot.id===id);
		this.ilotSelectionne.set(ilot);
	}

	deplacerJour(nombreDeJours: number): void {
		const date = this.dateReservation();
		date.setDate(date.getDate() + nombreDeJours);
		this.dateReservation.set(date);
		this.recupererIlotsEtBureaux();
	}

	allerDateDuJour(): void {
		this.dateReservation.set(new Date());
		this.recupererIlotsEtBureaux();
	}

	estDateDuJour(): boolean {
		return new Date().getDate() == this.dateReservation().getDate();
	}

	/**
	 * Détermine le titre pour savoir quelle est la durée de la réservation.
	 */
	determinerTitreModaleEnFonctionDuree(): string {
		let cleTraduction = 'RESERVER_UN_BUREAU.';

		if (this.etatVisionnage()===EtatPageZoneDeTravail.VISIONNER_TAUX) {
			cleTraduction += 'TITRE_VISUALISATION_RESERVATIONS';
			return this.translateService.instant(cleTraduction);
		}

		switch (this.duree()) {
			case Duree.JOURNEE: {
				cleTraduction += 'TITRE_ECRAN_RESERVATION_JOURNEE';
				break;
			}
			case Duree.MATIN: {
				cleTraduction += 'TITRE_ECRAN_RESERVATION_MATIN';
				break;
			}
			case Duree.APRES_MIDI: {
				cleTraduction += 'TITRE_ECRAN_RESERVATION_APRES_MIDI';
				break;
			}
		}

		return this.translateService.instant(cleTraduction);
	}

	/**
	 * Si l'ilot est sélectionné, peu importe s'il n'y a qu'un seul ou plusieurs ilots, on set à null l'ilôt sélectionné.
	 * @param zoneSelectionneeId l'id de la zone.
	 */
	onZoneSelectionnee(zoneSelectionneeId: number): void {
		if (this.estIlotSelectionne()) {
			this.ilotSelectionne.set(null);
			this.estIlotSelectionne.set(false);
		}

		this.zoneSelectionneeId = zoneSelectionneeId;
		this.recupererIlotsEtBureaux();
	}

	protected readonly Mode = Mode;
	protected readonly isNotNullOrUndefined = isNotNullOrUndefined;
}
